import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { InputPlaceComponent } from '@ildes/basic/input-place/input-place.component';
import { ApiManagerService } from '@ildes/services/api-manager.service';
import { GeolocationService } from '@ildes/services/geolocation.service';
import * as L from 'leaflet';
import { AdvancedSearchLuminaryDialogComponent } from '../advanced-search-luminary-dialog/advanced-search-luminary-dialog.component';
import { of } from 'rxjs';

const BOGOTA = {
    lat: 4.60971,
    lng: -74.08175
};

const iconDefault = L.icon({
    iconUrl: 'assets/icons/markers/blue_MarkerA.png',
    iconRetinaUrl: 'assets/icons/markers/blue_MarkerB.png',
    //shadowUrl: 'assets/icons/markers/blue_MarkerC.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [41, 41]
  });
  
L.Marker.prototype.options.icon = iconDefault;

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressBarModule,
    InputPlaceComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <mat-progress-bar mode="indeterminate" *ngIf="loading"></mat-progress-bar>
    <div class="fake-progress-content" *ngIf="!loading"></div>
    <div id="map" style="height: 100%;width: 100%;">
   
    </div>
    <div class="searcher">
        <input-place
            (selectPlace)="onSetPosition($event)"
            (onSetPosition)="onSetPosition($event)"
            hideGeopositionDetail="true"
            label="Ubicación"
            placeholder="CLL 33 45"
            dark
            [formControl]="location"
            appearance="fill"></input-place>
        <div class="rounded-button" *ngIf="private" (click)="advancedSearch()">
            <button mat-icon-button matSuffix >
                <mat-icon>settings</mat-icon>
            </button>
        </div>
    </div>
    
  `,
  styleUrls: [
    './map.component.css'
  ],
})
export class MapComponent implements OnInit, AfterViewInit {
    @Input('private') private = false;
    @Input('executeRecaptcha') executeRecaptcha;
    public luminaries = [];
    public location = new UntypedFormControl();
    private map!: L.Map
    private popup = L.popup();
    public loading;
    private width;
    private height;
    private radius;
    private markersLayer;
    private bounds;
    private zoom;
    private resetMarkers;
    private clusterLayer;
    private isProgramaticMode;
    private centerInNumber;
    private center = {
        lat: 4.669342, lng: -75.655218
    };
    private data;
    markers: L.Marker[] = [
      L.marker([4.669342, -75.655218]) // Dhaka, Bangladesh
    ];
  
    private advancedFilter;
    constructor(
        private apiManager: ApiManagerService,
        private geolocation: GeolocationService,
        private router: Router,
        private dialog: MatDialog
    ) { }
  
    ngOnInit() {
        
    }
  
    async ngAfterViewInit() {
        this.initMap();
        this.isProgramaticMode = true;
        await this.centerMap(false);

        setTimeout(() => {    
            this.boundIn();
            this.getCurrentBounds();
            this.listLuminaries();
            this.isProgramaticMode = false;
        }); 
    }

    getPoiIcon(technology: string, power: number) {
        // Normaliza el nombre de la clase según technology
        const techClass = ['led', 'sodio', 'mercurio'].indexOf(technology?.toLowerCase()?.trim()) >= 0 ? `poi-${technology.toLowerCase()}` : 'poi-default';
      
        return L.divIcon({
          className: `poi-marker ${techClass}`,
          html: `<span>${power || '•'}</span>`,
          iconSize: [24, 24],
          iconAnchor: [16, 24],
        });
    }

    getCurrentBounds() {
         // Obtener los límites actuales del mapa
       const bounds = this.map.getBounds();
       
       this.bounds = bounds.toBBoxString();
       if (this.map.getZoom() !== this.zoom) {
            this.resetMarkers = true;
       } else {
            this.resetMarkers = false;
       }
       this.zoom = this.map.getZoom();
       // Tomamos el lado "superior" del mapa
       const topLeft = bounds.getNorthWest();
       const topRight = bounds.getNorthEast();
       const bottomLeft = bounds.getSouthWest(); // lat/lng esquina inferior izquierda

       // Calculamos distancia entre esquinas (en metros)
       this.width = this.map.distance(topLeft, topRight);

       this.height = this.map.distance(bottomLeft, topLeft);

       this.radius = Math.max(this.width,this.height) / 2;
    }
  
  
    private initMap() {
        const baseMapURl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

        this.map = L.map('map', {
            minZoom: 0,     // zoom mínimo permitido
            maxZoom: 19,    // zoom máximo permitido
            zoomControl: false
        });
        L.control.zoom({
            position: 'bottomright'
        }).addTo(this.map);
        this.markersLayer = L.layerGroup().addTo(this.map);
        this.clusterLayer = L.layerGroup().addTo(this.map);
        //   this.markers[0].addTo(this.markersLayer);
        this.map.on('moveend', this.drawPois.bind(this));
        L.tileLayer(baseMapURl, { maxZoom: 19}).addTo(this.map);
    }

    drawPois(e) {
        const center = this.map.getCenter();
        this.center = {
            lat: center.lat,
            lng: center.lng,
        };
        this.getCurrentBounds();
        if (this.isProgramaticMode) {
            return;
        }
        this.listLuminaries();     
    }
  
    private boundIn() {
        // Create a boundary based on the markers
      let bounds = L.latLngBounds(this.markers.map(marker => marker.getLatLng()));
      
      // Fit the map into the boundary
      this.map.fitBounds(bounds);
      
    }
    private async centerMap(fetch = true) {
        try {
            const geolocationData = await this.geolocation.getPosition();
            if (!geolocationData) {
                this.onSetPosition(BOGOTA);
            } else {
                this.onSetPosition(geolocationData.positionData);
            }
        } catch {
            this.onSetPosition(BOGOTA);
        }
    }
    *performMarker(): Generator<any> {
        let i = 0;
        while (i < this.data.length) {
            const luminary = this.data[i];
            const geoposition = luminary.geometry.coordinates || [];

            let clusterIcon;
            let icon;
            if (luminary.properties.cluster) {
                // marcador de cluster
                const count = luminary.properties.point_count;
                clusterIcon = L.divIcon({
                    html: `<div class="cluster-marker">${count}</div>`,
                    className: '',
                    iconSize: [40, 40]
                  });
            } else {
                icon = this.getPoiIcon(luminary.properties.technology, luminary.properties.power);
            }
            i++;
            yield {
              luminary,
              clusterIcon,
              icon,
              id: luminary.properties.id,
              cluster: luminary.properties.cluster,
              technology: luminary.properties.technology,
              power: luminary.properties.power,
              address: luminary.properties.address,
              number: luminary.properties.number,
              position: {
                lat: geoposition[1],
                lng: geoposition[0],
              },
              options: {
                visible: true
              },
              // label: {
              //   color: technology === 'led' ? 'blue': 'red',
              // },
              //icon: this.getMarker({technology, power}),
              // title: 'Marker title ' + (this.markers.length + 1),
              info: 'Marker info ' + (this.markers.length + 1),
            }
          }
    }

    private fetchLuminaries(filter, token) {
        const listPois = this.private ? this.apiManager.listPois(filter): this.apiManager.lisPublicPois({latitude: this.center.lat ,longitude: this.center.lng, maxDistance: this.radius+50, bounds: this.bounds, zoom: this.zoom }, { error: true, timeout: false, token: token });

        return listPois;
    }
    private drawMarkers(data) {
        this.clusterLayer.clearLayers();
        if (this.resetMarkers) {
            this.luminaries = [];
            this.markersLayer.clearLayers();
        }
        if (!data?.length) {
            this.loading = false;
    
            return;
        }
        this.data = data;
        
        const generador = this.performMarker();
        while (true) {
            const marker = generador.next();
    
            if (marker.done) {
                break;
            }
            if (!marker.value.position.lat) {
                continue;
            }
            
            if (marker.value.cluster) {
                L.marker([marker.value.position.lat, marker.value.position.lng], { icon: marker.value.clusterIcon }).addTo(this.clusterLayer);
            } else {
                if (this.luminaries.indexOf(marker.value.id) >= 0) {
                    continue;
                }
                this.luminaries.push(marker.value.id);
                const Lmarker = L.marker([marker.value.position.lat, marker.value.position.lng], {icon: marker.value.icon} )
                    .addTo(this.markersLayer);
                if (this.private) {
                    const { address, number, technology, power, position } = marker.value;
                    Lmarker.bindPopup(`<b>${this.parseLuminary(number)}</b><p><ul><li>Dirección: ${address || 'No informado'}</li><li>Tecnología: ${technology}</li><li>Potencia: ${power}</li><li>Coords. (lat, lng): (${position.lat}, ${position.lng})</li></ul></p>`)
                        .on('popupopen', (e) => {
                            const link = e.popup.getElement().querySelector('.link-popup') as HTMLAnchorElement;
                            if (link) {
                            link.addEventListener('click', (event) => {
                                event.preventDefault(); // evita recarga
                                
                                this.router.navigate(['/welcome/contact-pqr'], {state: {id: marker.value.id}});
                            });
                            }
                        });
                } else {
                    Lmarker.bindPopup("<b>¿No funciona?</b><p>Puede reportar una incidencia a la operadora del municipio a través de un pqr.</p> <a routerLink='welcome/contact-pqr' class='link-popup'>Reportar pqr</a>")
                        .on('popupopen', (e) => {
                            const link = e.popup.getElement().querySelector('.link-popup') as HTMLAnchorElement;
                            if (link) {
                            link.addEventListener('click', (event) => {
                                // event.preventDefault(); // evita recarga
                                
                                this.router.navigate(['/welcome/contact-pqr']);
                            });
                            }
                        });
                }
                    
            }

            this.markers.push(marker.value);
        }

        if (this.centerInNumber && data[0]) {
            this.centerInNumber = false;
            this.isProgramaticMode = true;
            const geoposition = data[0].geometry.coordinates || [];
            this.onSetPosition({
                lat: geoposition[1],
                lng: geoposition[0]
            });

            this.isProgramaticMode = false;
        }
    
    }
    // TODO: mter throttle
    listLuminaries() {
        this.loading = true;
        const recaptcha = this.private ? of(null) : this.executeRecaptcha('mapMove');
        recaptcha.subscribe((token) => {
            const filter = {...this.getFilter(), ...{ latitude: this.center.lat , longitude: this.center.lng, maxDistance: this.radius+50, bounds: this.bounds, zoom: this.zoom }};
            this.fetchLuminaries(filter, token).subscribe((data) => {
                this.loading = false;
                this.drawMarkers(data);
            }, () => {
                this.loading = false;
            });
        }, () =>{
            this.loading = false;
        });
        
      }

    onSetPosition(position) {
        this.center = {
            lat: position.lat,
            lng: position.lng
        };
        this.markers = [
            L.marker([ this.center.lat,  this.center.lng]) // Dhaka, Bangladesh
        ];
        this.boundIn();
        this.getCurrentBounds();
    }  

    parseLuminary(number) {
        return number ? `Número de luminaria: ${number}`: 'Sin número';
    }

    advancedSearch() {
        const dialog = this.dialog.open(AdvancedSearchLuminaryDialogComponent, {
          width: '600px',
          height: '600px',
          panelClass: 'responsive-dialog',
          data: { filter: this.advancedFilter, options: { showLocationFilter: false } }
        });
    
        dialog.afterClosed().subscribe((data = { closed: false }) => {
    
          if (data.closed) {
            return;
          }
          this.advancedFilter = data;
          
          if (this.advancedFilter.project?.selected?.id) {
            const selectedProject = this.advancedFilter.project?.selected;
            // this.searchFormGroup.get('selectedProjectForm').setValue({selected: {id: selectedProject.id, name: selectedProject.name}, typed: selectedProject.name});
          }
          if (this.advancedFilter.number) {
            this.centerInNumber = true;
          } else {
            this.centerInNumber = false;
          }
          this.clusterLayer.clearLayers();
          
          this.luminaries = [];
          this.markersLayer.clearLayers();
          this.listLuminaries();
    
        });
    }

    private getFilter() {
        const filter: any = {};
    
        
        if (this.advancedFilter?.neighborhood) {
          filter.neighborhood = this.advancedFilter.neighborhood;
        }
    
        if (typeof this.advancedFilter?.withoutNumber === 'boolean' && this.advancedFilter?.withoutNumber) {
          filter.number = null;
        }
    
        if (this.advancedFilter?.location) {
          filter.latitude = this.advancedFilter.location.lat;
          filter.longitude = this.advancedFilter.location.lng;
          filter.maxDistance = 50;
        }
    
        if (this.advancedFilter?.number) {
          filter.number = this.advancedFilter.number;
        }
    
        if (this.advancedFilter?.temporalUse) {
          filter.temporalUse = this.advancedFilter.temporalUse;
        }
    
        if (this.advancedFilter?.technology) {
          filter.technology = this.advancedFilter.technology;
        }
    
        if (this.advancedFilter?.power) {
          filter.power = this.advancedFilter.power;
        }
    
        return filter;
    }

    ngOnDestroy() {
        if (this.map) {
        //   this.map.remove(); // elimina listeners, incluyendo de teclado
        }
    }
}

